import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { ShiftNames, UserPermission } from 'packages/workspace/modules/algebra/constants';
import { Can } from 'packages/workspace/modules/helpers/contexts/abilities';
import useInternalOnCalls from 'packages/workspace/services/hooks/useInternalOnCalls';
import useAbilitiesStore from 'packages/workspace/services/store/abilities-store';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdOutlinePhoneInTalk } from 'react-icons/md';
import * as yup from 'yup';
import { useYupValidationResolver } from '../../modules/schema';
import PhoneInput from '../common/PhoneInput';
import PhoneComponent from './PhoneComponent';

type ShoutOutProps = {
  open: boolean;
  onClose: () => void;
};

const ExternalCallSchema = yup.object({
  phone_number: yup
    .string()
    .matches(/^(\+1)\d{10}$/)
    .required('Required'),
});

const ShoutOut: React.FC<ShoutOutProps> = ({ open, onClose }) => {
  const { ability } = useAbilitiesStore();
  const { data: internalOnCallQuery, isSuccess, isError } = useInternalOnCalls(open && ability.can(UserPermission.EXTERNAL_CALL, ShiftNames.USER), 'external');
  const [callInitiated, setCallInitiated] = useState<boolean>(false);
  const {
    control,
    getValues,
    reset,
    formState: { errors },
  } = useForm<{ phone_number: string }>({
    mode: 'onChange',
    resolver: useYupValidationResolver(ExternalCallSchema),
  });

  const onDialogClose = (e: React.UIEvent) => {
    e.preventDefault();
    e.stopPropagation();
    reset();
    setCallInitiated(false);
    onClose();
  };

  return (
    <>
      <Can I={UserPermission.EXTERNAL_CALL} a={ShiftNames.USER}>
        <Dialog className="external-model-center-design" fullWidth maxWidth={'sm'} open={open} onClose={onDialogClose}>
          <DialogTitle>External Call</DialogTitle>
          <DialogContent className="center-div">
            <Box
              component="span"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                m: 'auto',
                width: 'fit-content',
              }}
            >
              <Controller
                name="phone_number"
                render={({ field }: { field: any }) => (
                  <PhoneInput
                    {...field}
                    hasError={Boolean(errors.phone_number)}
                    label="Enter Phone Number*"
                    inputProps={{
                      id: 'input-with-icon-textfield',
                      id: 'external_call_phone_number',
                    }}
                  />
                )}
                control={control}
                defaultValue={''}
                rules={{
                  required: true,
                }}
              />
            </Box>
            {isError && <span>Something Went Wrong, Please Close this and try again</span>}
            {callInitiated && internalOnCallQuery && (
              <PhoneComponent
                call_message="Device Connected, Call is in Progress"
                call_complete_message="Call is now complete, you can close this window"
                client_phone={internalOnCallQuery.phone}
                additionalParams={{ action: 'shoutout' }}
                force_disconnect={false}
                token={internalOnCallQuery.token}
                phone={getValues('phone_number')}
              />
            )}
          </DialogContent>
          <DialogActions>
            {isSuccess && internalOnCallQuery && (
              <Button disabled={callInitiated || Boolean(errors.phone_number) || !getValues('phone_number')} onClick={() => setCallInitiated(true)}>
                Connect
              </Button>
            )}
            <Button onClick={onDialogClose}>Close</Button>
          </DialogActions>
        </Dialog>
        <MdOutlinePhoneInTalk className="mr-1 text-base" />
        <span className="text-xs">External Calling</span>
      </Can>
    </>
  );
};

export default ShoutOut;
